import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LinkSimple, UserPlus, UsersThree, X } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EXPIRY = [["", "No expiry"], ["1", "1 day"], ["7", "7 days"], ["30", "30 days"]];
const expiryIso = (d) => (d ? new Date(Date.now() + Number(d) * 86400000).toISOString() : null);

function Upgrade() {
  return (
    <p className="text-xs rounded-xl bg-amber-50 border border-amber-200 p-3 text-rf-ink" data-testid="share-upgrade-prompt">
      Live editing and sharing with chosen people are part of every paid plan. You can still send a view-only link or a snapshot.{" "}
      <Link to="/pricing" className="font-bold text-rf-orange underline">See plans</Link>
    </p>
  );
}

function AccessRow({ s, paid, onChanged }) {
  const setRole = async (role) => {
    try { await api.patch(`/shares/${s.share_id}`, { role }); onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't change role"); }
  };
  const revoke = async () => {
    try { await api.delete(`/shares/${s.share_id}`); toast.success("Access removed"); onChanged(); } catch { toast.error("Couldn't remove access"); }
  };
  return (
    <li className="flex items-center gap-2 py-2" data-testid={`share-row-${s.share_id}`}>
      <span className="flex-1 min-w-0 truncate text-sm font-semibold">{s.display}{s.target_type === "email" ? " (invited)" : ""}
        {s.expires_at && <span className="ml-1 text-xs text-rf-ink-soft">· until {new Date(s.expires_at).toLocaleDateString()}</span>}
      </span>
      <select value={s.role} onChange={(e) => setRole(e.target.value)} disabled={!paid && s.role === "view"}
              data-testid={`share-role-${s.share_id}`} className="input-rf !py-1 !w-auto text-xs">
        <option value="view">Can view</option>
        <option value="edit">Can edit</option>
      </select>
      <button type="button" onClick={revoke} title="Remove access" data-testid={`share-revoke-${s.share_id}`} className="p-1.5 rounded-full hover:bg-red-50 text-red-700"><X size={14} weight="bold" /></button>
    </li>
  );
}

// Owner-only sharing for a shopping list or meal planner (people, Family Plan members, invite links).
export const ShareDialog = ({ open, onOpenChange, resourceType, resourceId, label, onChanged, children }) => {
  const [data, setData] = useState(null);
  const [who, setWho] = useState("");
  const [role, setRole] = useState("view");
  const [expiry, setExpiry] = useState("");
  const [fam, setFam] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/shares", { params: { resource_type: resourceType, resource_id: resourceId } })
    .then(({ data: d }) => setData(d)).catch(() => setData({ shares: [], paid: false, family_members: [] }));
  useEffect(() => { if (open && resourceId) load(); }, [open, resourceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (targets) => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/shares", { resource_type: resourceType, resource_id: resourceId, role, expires_at: expiryIso(expiry), targets });
      if (r.link_url) {
        if (navigator.share) { try { await navigator.share({ title: label, text: `Here's ${label} on Recipe Filer`, url: r.link_url }); } catch { /* dismissed */ } }
        else { await navigator.clipboard?.writeText(r.link_url); toast.success("Invite link copied — paste it into WhatsApp, a text or an email"); }
      } else toast.success("Shared");
      setWho(""); setFam([]);
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't share"); } finally { setBusy(false); }
  };

  const paid = !!data?.paid;
  const meal = resourceType === "meal_plan";
  const lockedPeople = !paid;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-rf-cream max-w-lg max-h-[90vh] overflow-y-auto" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black text-rf-ink">Share {label}</DialogTitle>
          <DialogDescription className="text-rf-ink-soft">People you invite never need to pay. Only you can change who has access.</DialogDescription>
        </DialogHeader>
        {!data ? <p className="text-sm text-rf-ink-soft">Loading…</p> : (
          <div className="space-y-4">
            <div className="flex gap-2">
              <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="share-new-role" className="input-rf !py-1.5 text-sm">
                <option value="view">Can view</option>
                <option value="edit" disabled={!paid}>Can edit{!paid ? " (paid)" : ""}</option>
              </select>
              <select value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="share-new-expiry" className="input-rf !py-1.5 text-sm">
                {EXPIRY.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            {lockedPeople && <Upgrade />}
            <form onSubmit={(e) => { e.preventDefault(); if (who.trim()) send([{ type: "user", identifier: who }]); }} className="flex gap-2">
              <input value={who} onChange={(e) => setWho(e.target.value)} disabled={lockedPeople} placeholder="Email or username"
                     data-testid="share-person-input" className="input-rf flex-1" />
              <button type="submit" disabled={busy || lockedPeople || !who.trim()} data-testid="share-person-submit" className="btn-ink !py-2 !px-4 text-sm inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"><UserPlus size={16} /> Add</button>
            </form>
            {data.family_members.length > 0 && (
              <div className="rounded-xl bg-white p-3" data-testid="share-family">
                <p className="text-xs font-bold uppercase tracking-wider text-rf-ink-soft mb-2 inline-flex items-center gap-1"><UsersThree size={14} /> Family Plan members</p>
                {data.family_members.map((m) => (
                  <label key={m.user_id} className="flex items-center gap-2 text-sm py-1">
                    <input type="checkbox" disabled={lockedPeople} checked={fam.includes(m.user_id)} data-testid={`share-family-${m.user_id}`}
                           onChange={(e) => setFam(e.target.checked ? [...fam, m.user_id] : fam.filter((x) => x !== m.user_id))} /> {m.name}
                  </label>
                ))}
                <div className="flex gap-2 mt-2">
                  <button type="button" disabled={busy || lockedPeople || !fam.length} onClick={() => send([{ type: "family", user_ids: fam }])} data-testid="share-family-selected" className="btn-outline-ink !py-1.5 !px-3 text-xs">Share with selected</button>
                  <button type="button" disabled={busy || lockedPeople} onClick={() => send([{ type: "family", user_ids: "all" }])} data-testid="share-family-all" className="btn-outline-ink !py-1.5 !px-3 text-xs">All members</button>
                </div>
              </div>
            )}
            <button type="button" disabled={busy || (meal && !paid) || (!paid && role === "edit")} onClick={() => send([{ type: "link" }])}
                    data-testid="share-link-create" className="btn-amber w-full justify-center inline-flex items-center gap-2">
              <LinkSimple size={18} /> {role === "edit" ? "Create edit link" : "Create view-only link"} — no account needed
            </button>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-rf-ink-soft">Who has access</p>
              {data.shares.length === 0 ? <p className="text-sm text-rf-ink-soft mt-1" data-testid="share-none">Only you.</p> : (
                <ul className="divide-y divide-rf-ink/10" data-testid="share-access-list">
                  {data.shares.map((s) => <AccessRow key={s.share_id} s={s} paid={paid} onChanged={() => { load(); onChanged?.(); }} />)}
                </ul>
              )}
            </div>
            {children}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
